import { Box, Button, CircularProgress, Fade, Tooltip } from "@material-ui/core";
import { Help } from "@material-ui/icons";
import { useEffect, useMemo, useState } from "react";
import { useDebouncedHandler } from "../../Helpers/debounced";
import { useFeaturesEnabledQuery } from "../../Queries/FeaturesEnabledQueries";
import { FeaturesEnabledType } from "../../Types/FeaturesEnabled";
import { SearchInput } from "./FeaturesEnabledPage";
import { getColumnUseRate, sortUsageData, usageTableData, UsageTableRow } from "./Functions";
import SharedTable from "./SharedTable";
import { download, generateCsv } from "export-to-csv";
import { createCsvConfigAndFormatter } from "../../Helpers/csvExportHelpers";

const columns = [
  { label: "Client Name", propName: "clientName" },
  { label: "Site URL", propName: "siteUrl" },
  { label: "CRM", propName: "CRM" },
  { label: "CRM Lite", propName: "CRM Lite" },
  { label: "Events", propName: "Events" },
  { label: "Events RSVP", propName: "Events RSVP" },
  { label: "Featured Events On Home", propName: "Featured Events On Home" },
  { label: "Featured Jobs (Groups) on Career Areas", propName: "Featured Jobs (Groups) on Career Areas" },
  { label: "Featured Jobs (Groups) On Home", propName: "Featured Jobs (Groups) On Home" },
  { label: "Featured Jobs (Single) on Career Areas", propName: "Featured Jobs (Single) on Career Areas" },
  { label: "Featured Jobs (Single) On Home", propName: "Featured Jobs (Single) On Home" },
  { label: "Get Directions", propName: "Get Directions" },
  { label: "Locations Page", propName: "Locations Page" },
  { label: "NAS Job Alerts", propName: "NAS Job Alerts" },
  { label: "NAS Talent Network", propName: "NAS Talent Network" },
  { label: "NMP Talent Network Export", propName: "NMP Talent Network Export" },
  { label: "Similar Jobs", propName: "Similar Jobs" },
];

export default function SiteFeaturesTable() {
  const [querySearch, setQuerySearch] = useState<string>("");
  const [sortedProperty, setSortedProperty] = useState("clientName");
  const [ascending, setAscending] = useState(true);
  const [sortedData, setSortedData] = useState<UsageTableRow[]>([]);

  const usageRequest = useMemo(
    () => ({ type: FeaturesEnabledType.SiteFeatures, search: querySearch }),
    [querySearch],
  );
  const {
    data: usage = [],
    isLoading,
    isFetching,
    isError,
  } = useFeaturesEnabledQuery(usageRequest, (data) => usageTableData(data));
  const { data: lastUpdated = "" } = useFeaturesEnabledQuery(usageRequest, (data) =>
    data ? new Date(data.lastUpdated).toLocaleString() : "",
  );

  const handleSearchChange = useDebouncedHandler((value: string) => setQuerySearch(value), 300);

  function sortTable(column: TableColumn) {
    if (column.propName === sortedProperty) {
      setAscending(!ascending);
    } else {
      setSortedProperty(column.propName);
      setAscending(true);
    }
  }

  const columnsWithRates = useMemo(
    () =>
      columns.map((column) => {
        if (column.propName === "clientName" || column.propName === "siteUrl") {
          return column;
        }
        return {
          ...column,
          label: `${column.label} (${getColumnUseRate(sortedData, column.propName)})`,
        };
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(sortedData)],
  );

  const { csvOptions, formatRows } = createCsvConfigAndFormatter(
    columnsWithRates,
    "Site_Features_Report",
    "Site_Features_Report",
  );

  const handleExport = () => {
    const csv = generateCsv(csvOptions)(formatRows(sortedData));
    download(csvOptions)(csv);
  };

  useEffect(() => {
    if (usage.length !== 0) {
      setSortedData(sortUsageData(usage, sortedProperty, ascending));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(usage), sortedProperty, ascending]);

  return (
    <>
      <Box
        display="flex"
        justifyContent="flex-start"
        alignItems="center"
        gridGap="1em"
        width="98%"
        marginBottom="1em"
      >
        <SearchInput value={querySearch} onChange={handleSearchChange} />
        <Box>
          <Tooltip
            arrow
            placement="right"
            title="Indicates which career site features are being used for the given Careers Client. Column headers include the percentage of Clients using each feature."
          >
            <Help color="disabled" />
          </Tooltip>
        </Box>
        <Button variant="contained" color="primary" onClick={handleExport}>
          Export Data
        </Button>
        <Fade in={isFetching} unmountOnExit>
          <Box sx={{ position: "relative", alignSelf: "center" }}>
            <CircularProgress size="1.5rem" />
          </Box>
        </Fade>
        {lastUpdated.length > 0 ? (
          <Box marginLeft="auto" marginTop="auto" fontSize="0.85em">
            <strong>Last Updated:</strong> {lastUpdated}
          </Box>
        ) : null}
      </Box>
      <SharedTable
        data={sortedData}
        columns={columnsWithRates}
        isLoading={isLoading}
        sortedProperty={sortedProperty}
        onSort={sortTable}
        ascending={ascending}
        error={isError}
      />
    </>
  );
}
